/**
 * NOLAS 2026, © Mika (Mikaël FOURNY). Fork de la plateforme TNE 2026.
 * src/i18n/fr.parcours.ts
 *
 * Rôle :
 *   Sous-catalogue i18n du chantier « parcours suggéré à l'arrivée »
 *   (carte « Par où commencer »). Fusionné dans `messagesFr` par
 *   `i18n/fr.ts`, donc utilisable comme n'importe quelle clé.
 *
 * Pourquoi un fichier SÉPARÉ : `fr.ts` est LA dépendance partagée du dépôt
 *   et trois chantiers tournent en parallèle. Un catalogue par chantier
 *   supprime la collision par construction (leçon du lot Console C9).
 *
 * ⚠ Règles appliquées ici :
 *   • Aucune clé du noyau n'est redéfinie : `fr.subcatalogs.test.ts` échoue
 *     sinon. Les libellés dont ce chantier a besoin et qui existent déjà
 *     (`jobs.matching.improve.*`, `jobs.matching.viewAll`, les onze
 *     `jobs.matching.reason.*`) sont RÉUTILISÉS, pas recopiés.
 *   • Aucun nom d'événement, de lieu, de date ni d'organisateur.
 *   • Aucun mot du lexique en dur. Le concept « celui qui tient un stand »
 *     est configurable par événement (« exposant », « entreprise »,
 *     « organisme »…) : ces libellés sont donc écrits de façon à ne pas
 *     avoir à le nommer du tout. Le nom réel de la structure vient de la
 *     base, il est affiché tel quel par l'écran.
 *   • Apostrophe typographique `’`, et jamais d'apostrophe DROITE devant un
 *     `{token}` (elle ouvrirait une séquence d'échappement ICU).
 *
 * Mikael - chef de projet - 2026-09-10
 */

export const messagesParcoursFr: Record<string, string> = {
  // ────────────────────────────────────────────────────────────────────────
  // Carte « Par où commencer » : l'écran d'arrivée
  // ────────────────────────────────────────────────────────────────────────
  'startHere.title': 'Par où commencer',
  'startHere.subtitle':
    'Un ordre de visite proposé à partir de ce que vous avez indiqué dans votre profil. Suivez-le, ou n’en tenez pas compte.',

  /**
   * Ligne de justification, UNE par étape. `{reasons}` est déjà composé côté
   * écran à partir des libellés de raison du catalogue : la phrase reste donc
   * traduisible d'un bloc, et aucune raison n'est écrite deux fois.
   */
  'startHere.step.line': '{jobTitle} · {reasons}',
  'startHere.step.aria': 'Étape {rank} sur {total} : {company}',
  'startHere.step.otherJobs':
    '{count, plural, one {1 autre poste correspond aussi} other {# autres postes correspondent aussi}}',

  // Action de retrait. Le parcours SUGGÈRE : il doit pouvoir disparaître en
  // un geste, sans justification demandée et sans rien perdre par ailleurs.
  'startHere.dismiss': 'Ne pas afficher ce parcours',
  'startHere.dismiss.aria': 'Retirer la proposition de parcours',

  /**
   * Mention de transparence. Elle n'est pas décorative : le tri de
   * candidatures est un usage à haut risque au sens du règlement européen sur
   * l’intelligence artificielle, et dire d’où vient une proposition est la
   * première des garanties dues à la personne.
   */
  'startHere.footnote':
    'Proposition établie à partir de votre profil et des postes publiés, sans intelligence artificielle et sans note cachée. Elle n’engage personne : vous décidez de votre visite.',

  // ── Rien à proposer : on le dit, et on dit pourquoi ──────────────────────
  // Deux causes, deux messages, deux actions différentes.
  'startHere.empty.weak':
    'Nous ne vous proposons pas de parcours : votre profil ne dit pas encore ce que vous recherchez, et vous envoyer quelque part au hasard ne vous rendrait pas service.',
  'startHere.empty.noMatch':
    'Aucun point de départ ne se dégage de votre profil pour l’instant. Les postes publiés évoluent jusqu’au dernier moment : parcourez-les librement.',
};
